import { useState } from "react";

import { Warning } from "@material-ui/icons";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import { Button, Modal } from "antd";
import styled from "styled-components";

import { useFeatureGatedFilter } from "hooks/useFeatureGatedFilter";

import { Dashboard } from "models/workspace";

import { ErrorBoundary } from "components/base";
import { IconSpinner } from "components/icons";
import { SectionBody } from "components/user-settings/components/workspace/WorkspaceManagement.styles";
import { useUserContext } from "components/user/context";
import { useWorkspaceContext } from "components/workspace/hooks/useWorkspaceContext";

import DashboardTable from "./DashboardTable/DashboardTable";
import { useAddDashboardToWorkspace } from "./hooks/useAddDashboardToWorkspace";
import { useCloneDashboard } from "./hooks/useCloneDashboard";
import useDashboards from "./hooks/useDashboards";
import { useDeleteDashboard } from "./hooks/useDeleteDashboard";
import { useHideDashboard } from "./hooks/useHideDashboard";
import { useScrollToHighlighted } from "./hooks/useScrollToHighlighted";

export default function AddDashboard() {
  const [visible, setVisible] = useState(false);
  const [addError, setAddError] = useState("");
  const [highlightedDashboardId, setHighlightedDashboardId] = useState<string | null>(
    null
  );
  const { workspace } = useWorkspaceContext();

  const { data, loading: isLoading } = useDashboards();

  useScrollToHighlighted(highlightedDashboardId);

  const [userContext] = useUserContext();
  const { filterDashboards } = useFeatureGatedFilter();

  const allDashboards = filterDashboards(data?.dashboards ?? []);

  const canUserAddDashboardToOrgWorkspace =
    workspace?.type.toLocaleLowerCase() !== "organization" ||
    (workspace?.type.toLocaleLowerCase() === "organization" &&
      userContext?.isAtLeastPowerUser);

  const addDashboardToWorkspace = useAddDashboardToWorkspace({
    onSuccess: () => {
      setAddError("");
    },
    onError: () => {
      setAddError("Failed to add the dashboard to the workspace");
    }
  });

  const cloneDashboard = useCloneDashboard({
    highlightConfig: {
      setHighlightedDashboard: setHighlightedDashboardId
    }
  });

  const handleCloneDashboard = async (
    dashboard: Dashboard,
    title: string,
    type: string
  ): Promise<void> => {
    await cloneDashboard.mutate(dashboard, title, type, workspace);
  };

  const hideDashboard = useHideDashboard();

  const deleteDashboard = useDeleteDashboard();

  const openModal = () => {
    setAddError("");
    setVisible(true);
  };

  const closeModal = () => {
    setVisible(false);
  };

  const selectedDashboards =
    workspace?.dashboards
      ?.map((dashboard) => {
        return allDashboards?.find((d) => d.dashboardId === dashboard.dashboardId);
      })
      .filter((d) => d !== undefined) || [];

  // Filter out the dashboards that are in the selectedDashboards list
  // and exclude personal dashboards for organization workspaces
  const otherDashboards = allDashboards.filter(
    (dashboard) =>
      !selectedDashboards.some((d) => d.dashboardId === dashboard.dashboardId) &&
      (workspace?.type?.toLocaleLowerCase() !== "organization" ||
        dashboard.type !== "Personal")
  );

  return (
    <RootContainer>
      <ErrorBoundary>
        <Modal
          open={visible}
          width={850}
          wrapClassName={"add-dashboard-modal-root"}
          onOk={closeModal}
          onCancel={closeModal}
          closable={false}
          footer={[
            <Button
              key="cancel"
              onClick={closeModal}
              data-testid="add-dashboard-cancel-button">
              Close
            </Button>
          ]}>
          <>
            <h5>Add Dashboard</h5>
            <AddDashboardHelp>
              Select the dashboard you wish to add to your workspace. You can also create
              a new copy of any existing dashboard.
            </AddDashboardHelp>
            {!canUserAddDashboardToOrgWorkspace && (
              <WarningContainer>
                <Warning style={{ top: 3, paddingRight: 2 }} />
                Please contact an Admin or Power User to make changes to an organization
                workspace
              </WarningContainer>
            )}
            {isLoading && <IconSpinner />}
            {!isLoading && (
              <>
                <DashboardsContainer>
                  <SectionBody>
                    {selectedDashboards.length > 0 && (
                      <DashboardTable
                        dashboardList={selectedDashboards}
                        defaultSort={null}
                        hideDashboard={
                          canUserAddDashboardToOrgWorkspace && workspace
                            ? (dashboardId: string) =>
                                hideDashboard.mutate(dashboardId, workspace)
                            : null
                        }
                        highlightedDashboardId={highlightedDashboardId}
                        setHighlightedDashboard={setHighlightedDashboardId}
                        deleteDashboard={
                          workspace
                            ? (dashboardId: string) =>
                                deleteDashboard.mutate(dashboardId, workspace)
                            : undefined
                        }
                        cloneDashboard={
                          canUserAddDashboardToOrgWorkspace
                            ? handleCloneDashboard
                            : undefined
                        }
                        tableSettings={{
                          title: "Selected Dashboards"
                        }}
                        workspace={workspace}
                      />
                    )}
                  </SectionBody>

                  <SectionBody>
                    <DashboardTable
                      dashboardList={otherDashboards}
                      addDashboard={
                        canUserAddDashboardToOrgWorkspace
                          ? addDashboardToWorkspace.mutate
                          : null
                      }
                      deleteDashboard={(dashboardId: string) =>
                        workspace && deleteDashboard.mutate(dashboardId, workspace)
                      }
                      cloneDashboard={
                        canUserAddDashboardToOrgWorkspace
                          ? handleCloneDashboard
                          : undefined
                      }
                      highlightedDashboardId={highlightedDashboardId}
                      setHighlightedDashboard={setHighlightedDashboardId}
                      tableSettings={{
                        title: "Available Dashboards"
                      }}
                      workspace={workspace}
                    />
                  </SectionBody>
                </DashboardsContainer>
              </>
            )}
            {addError !== "" && <ErrorContainer>{addError}</ErrorContainer>}
          </>
        </Modal>
      </ErrorBoundary>

      <StyledButton
        type="link"
        icon={<AddCircleIcon fontSize="large" />}
        onClick={openModal}
        data-testid="add-dashboard-button"
      />
    </RootContainer>
  );
}

const RootContainer = styled.div``;

const DashboardsContainer = styled.div`
  max-height: 550px;
  padding: 0 10px;
  overflow-x: auto;
`;

const AddDashboardHelp = styled.div`
  padding-top: 8px;
  padding-bottom: 8px;
`;

const ErrorContainer = styled.div`
  display: inline-flex;
  color: var(--color-danger);
  font-weight: var(--fontWeightMedium);
  padding-bottom: 8px;
`;

const WarningContainer = styled.div`
  display: inline-flex;
  color: var(--orange);
  font-weight: var(--fontWeightMedium);
  padding-bottom: 8px;
`;

const StyledButton = styled(Button)`
  // --ant-primary-color: var(--color-icon);
  color: var(--color-icon);
  &:focus {
    color: var(--color-icon);
  }
  &:hover {
    color: var(--color-text) !important;
  }
  &:focus:not(:hover) {
    color: var(--color-icon);
  }
`;
